import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload, User, Mail, Image as ImageIcon, Instagram, Twitter, ArrowRight } from "lucide-react";
import SectionBackground from "@/components/layout/SectionBackground";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { theme } from "@/theme";

interface OnboardingFormData {
  fullName: string;
  username: string;
  email: string;
  profilePhoto: File | null;
  instagram: string;
  twitter: string;
}

const Onboarding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('courseId');
  
  const [formData, setFormData] = useState<OnboardingFormData>({
    fullName: '',
    username: '',
    email: '',
    profilePhoto: null,
    instagram: '',
    twitter: '',
  });
  
  const [currentStep, setCurrentStep] = useState(1);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, profilePhoto: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // TODO: Implement actual Supabase save logic
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Redirect to course page or Artroverse
    if (courseId) {
      // TODO: Replace with actual course URL
      window.location.href = `https://artoverse.club/courses/${courseId}`;
    } else {
      navigate('/courses');
    }
  };

  const isStepValid = () => {
    if (currentStep === 1) {
      return formData.fullName.trim() !== '' && formData.email.trim() !== '';
    }
    if (currentStep === 2) {
      return formData.username.trim() !== '';
    }
    return true;
  };

  return (
    <main className="relative min-h-screen bg-black pt-20 lg:pt-[88px] flex items-center justify-center">
      <SectionBackground />
      
      <div className="relative w-full max-w-2xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{
                fontFamily: theme.typography.fontFamily,
                color: theme.colors.text,
              }}
            >
              Welcome to{" "}
              <span style={{ color: theme.colors.accent }}>Endless Academy</span>
            </h1>
            <p
              className="text-lg"
              style={{ color: theme.colors.secondary }}
            >
              Let's set up your profile to get started
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                      currentStep >= step
                        ? 'text-white'
                        : 'text-gray-500'
                    }`}
                    style={{
                      backgroundColor: currentStep >= step ? theme.colors.accent : theme.colors.bgHover,
                      border: `2px solid ${currentStep >= step ? theme.colors.accent : theme.colors.border}`,
                    }}
                  >
                    {step}
                  </div>
                  {step < 3 && (
                    <div
                      className="w-16 h-1 mx-2"
                      style={{
                        backgroundColor: currentStep > step ? theme.colors.accent : theme.colors.border,
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <Card
            style={{
              background: "rgba(10, 10, 10, 0.9)",
              backdropFilter: "blur(20px)",
              border: `1px solid ${theme.colors.accent}30`,
            }}
          >
            <CardHeader>
              <CardTitle style={{ color: theme.colors.text }}>
                {currentStep === 1 && "Basic Information"}
                {currentStep === 2 && "Choose Your Username"}
                {currentStep === 3 && "Profile Picture & Social Links"}
              </CardTitle>
              <CardDescription style={{ color: theme.colors.secondary }}>
                {currentStep === 1 && "Tell us a bit about yourself"}
                {currentStep === 2 && "Pick a unique username for your profile"}
                {currentStep === 3 && "Add a photo and connect your social accounts (optional)"}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Step 1: Basic Info */}
                {currentStep === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <div>
                      <Label htmlFor="fullName" style={{ color: theme.colors.text }}>
                        Full Name *
                      </Label>
                      <div className="relative mt-2">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <Input
                          id="fullName"
                          type="text"
                          required
                          value={formData.fullName}
                          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          className="pl-10"
                          placeholder="Enter your full name"
                          style={{
                            background: "rgba(0, 0, 0, 0.5)",
                            borderColor: theme.colors.border,
                            color: theme.colors.text,
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email" style={{ color: theme.colors.text }}>
                        Email Address *
                      </Label>
                      <div className="relative mt-2">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="pl-10"
                          placeholder="your.email@example.com"
                          style={{
                            background: "rgba(0, 0, 0, 0.5)",
                            borderColor: theme.colors.border,
                            color: theme.colors.text,
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Username */}
                {currentStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div>
                      <Label htmlFor="username" style={{ color: theme.colors.text }}>
                        Username *
                      </Label>
                      <div className="relative mt-2">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">@</span>
                        <Input
                          id="username"
                          type="text"
                          required
                          value={formData.username}
                          onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                          className="pl-10"
                          placeholder="yourusername"
                          style={{
                            background: "rgba(0, 0, 0, 0.5)",
                            borderColor: theme.colors.border,
                            color: theme.colors.text,
                          }}
                        />
                      </div>
                      <p className="text-xs mt-2" style={{ color: theme.colors.textMuted }}>
                        Only lowercase letters, numbers, and underscores
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Profile Photo & Social */}
                {currentStep === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    {/* Profile Photo Upload */}
                    <div>
                      <Label style={{ color: theme.colors.text }}>Profile Photo (Optional)</Label>
                      <div className="mt-2 flex flex-col items-center gap-4">
                        {previewImage ? (
                          <div className="relative">
                            <img
                              src={previewImage}
                              alt="Profile preview"
                              className="w-32 h-32 rounded-full object-cover"
                              style={{ border: `3px solid ${theme.colors.accent}` }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setPreviewImage(null);
                                setFormData({ ...formData, profilePhoto: null });
                              }}
                              className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <label
                            htmlFor="profilePhoto"
                            className="w-32 h-32 rounded-full flex flex-col items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                            style={{
                              backgroundColor: theme.colors.bgHover,
                              border: `2px dashed ${theme.colors.border}`,
                            }}
                          >
                            <Upload size={32} style={{ color: theme.colors.accent }} />
                            <span className="text-xs mt-2" style={{ color: theme.colors.textMuted }}>
                              Upload Photo
                            </span>
                          </label>
                        )}
                        <input
                          id="profilePhoto"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </div>
                    </div>

                    {/* Social Handles */}
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="instagram" style={{ color: theme.colors.text }}>
                          Instagram (Optional)
                        </Label>
                        <div className="relative mt-2">
                          <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                          <Input
                            id="instagram"
                            type="text"
                            value={formData.instagram}
                            onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                            className="pl-10"
                            placeholder="@yourusername"
                            style={{
                              background: "rgba(0, 0, 0, 0.5)",
                              borderColor: theme.colors.border,
                              color: theme.colors.text,
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="twitter" style={{ color: theme.colors.text }}>
                          Twitter/X (Optional)
                        </Label>
                        <div className="relative mt-2">
                          <Twitter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                          <Input
                            id="twitter"
                            type="text"
                            value={formData.twitter}
                            onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                            className="pl-10"
                            placeholder="@yourusername"
                            style={{
                              background: "rgba(0, 0, 0, 0.5)",
                              borderColor: theme.colors.border,
                              color: theme.colors.text,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between pt-6">
                  {currentStep > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCurrentStep(currentStep - 1)}
                      style={{
                        borderColor: theme.colors.border,
                        color: theme.colors.text,
                      }}
                    >
                      Back
                    </Button>
                  )}
                  
                  <div className="ml-auto">
                    {currentStep < 3 ? (
                      <Button
                        type="button"
                        onClick={() => setCurrentStep(currentStep + 1)}
                        disabled={!isStepValid()}
                        style={{
                          background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
                          color: theme.colors.text,
                        }}
                      >
                        Next
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
                          color: theme.colors.text,
                        }}
                      >
                        {isSubmitting ? 'Creating Profile...' : 'Complete Setup'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </main>
  );
};

export default Onboarding;



